/* Collection and search filtering: re-renders the results with the Section Rendering API,
   keeps the URL shareable with history.pushState, and falls back to a normal form submit. */
class MsFilters extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.form = document.getElementById(this.dataset.form);
    this.panel = this.querySelector('[data-filters-panel]');
    this.opener = this.querySelector('[data-open-filters]');
    if (!this.form) return;

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.apply();
      this.closePanel();
    });
    this.addEventListener('change', (event) => {
      if (event.target.closest('select[name="sort_by"]')) return this.apply();
      if (event.target.closest('.ms-filters__form') && this.isDesktop()) this.apply();
    });
    this.addEventListener('click', (event) => {
      const link = event.target.closest('[data-chip], [data-clear-all], .ms-pagination a');
      if (link && link.href) {
        event.preventDefault();
        this.render(link.href, true, true);
        if (link.closest('.ms-pagination')) this.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        return;
      }
      if (event.target.closest('[data-open-filters]')) this.openPanel();
      if (event.target.closest('[data-close-filters]')) this.closePanel();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.panel?.hasAttribute('data-open')) {
        event.stopPropagation();
        this.closePanel();
      }
    });
    this.onPop = () => this.render(window.location.href, false);
    window.addEventListener('popstate', this.onPop);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPop);
    this.controller?.abort();
  }

  isDesktop() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  /* Build the shareable URL from the form, dropping empty fields, then render it */
  apply() {
    const url = new URL(this.form.action, window.location.origin);
    url.search = '';
    const sort = this.querySelector('select[name="sort_by"]');
    new FormData(this.form).forEach((value, key) => {
      if (String(value).trim() !== '') url.searchParams.append(key, value);
    });
    if (sort?.value) url.searchParams.set('sort_by', sort.value);
    this.render(url.toString(), true);
  }

  /* focus: move keyboard focus to the result count. Only for link-driven renders (chips, pagination, clear all),
     where the clicked link has just been replaced. Ticking a filter keeps focus on the checkbox. */
  async render(href, push, focus = false) {
    this.controller?.abort();
    this.controller = new AbortController();
    const results = this.querySelector('[data-results]');
    results?.setAttribute('aria-busy', 'true');
    const url = new URL(href, window.location.origin);
    url.searchParams.set('section_id', this.sectionId);
    try {
      const response = await fetch(url.toString(), { signal: this.controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const next = new DOMParser().parseFromString(await response.text(), 'text/html');
      this.swap(next);
      if (push) {
        const visible = new URL(href, window.location.origin);
        visible.searchParams.delete('section_id');
        window.history.pushState({}, '', visible.toString());
      }
      if (focus) this.querySelector('[data-count]')?.focus({ preventScroll: true });
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.href = href;
    } finally {
      results?.setAttribute('aria-busy', 'false');
    }
  }

  /* Replace the results and count; sync the filter inputs so counts and disabled states stay true */
  swap(next) {
    const swapNode = (selector) => {
      const current = this.querySelector(selector);
      const fresh = next.querySelector(selector);
      if (current && fresh) current.innerHTML = fresh.innerHTML;
    };
    swapNode('[data-results]');
    swapNode('[data-count]');
    next.querySelectorAll('.ms-filters__form input').forEach((fresh) => {
      const current = fresh.id ? document.getElementById(fresh.id) : this.form.elements[fresh.name];
      if (!current) return;
      if (fresh.type === 'checkbox') {
        current.checked = fresh.checked;
        current.disabled = fresh.disabled;
      } else {
        current.value = fresh.value;
      }
    });
    next.querySelectorAll('[data-filter-count]').forEach((fresh) => {
      const current = this.querySelector(`[data-filter-count="${fresh.dataset.filterCount}"]`);
      if (current) current.textContent = fresh.textContent;
    });
  }

  /* Mobile filter sheet: opens as a dialog, traps focus, returns it to the button on close */
  openPanel() {
    if (!this.panel || this.isDesktop()) return;
    this.panel.setAttribute('data-open', '');
    /* Only the phone sheet is a modal; the desktop sidebar stays part of the page */
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-modal', 'true');
    this.opener?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('ms-scroll-lock');
    this.trap = (event) => this.trapFocus(event);
    document.addEventListener('keydown', this.trap);
    requestAnimationFrame(() => this.panel.querySelector('[data-close-filters]')?.focus());
  }

  closePanel() {
    if (!this.panel?.hasAttribute('data-open')) return;
    this.panel.removeAttribute('data-open');
    this.panel.removeAttribute('role');
    this.panel.removeAttribute('aria-modal');
    this.opener?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('ms-scroll-lock');
    document.removeEventListener('keydown', this.trap);
    this.opener?.focus();
  }

  trapFocus(event) {
    if (event.key !== 'Tab') return;
    const focusable = [...this.panel.querySelectorAll('a[href], button, input:not([disabled]), summary')].filter(
      (el) => el.offsetParent !== null
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
customElements.define('ms-filters', MsFilters);
